import React from "react";

// Category gradient palette — no real photos, premium & consistent.
const PALETTES = {
  bollywood: ["#FF3B7C", "#8B2FFF"],
  cricket:   ["#00FF66", "#00D4FF"],
  creator:   ["#FFD600", "#FF6B00"],
  reality:   ["#FF0055", "#B026FF"],
  music:     ["#00F0FF", "#B026FF"],
  sports:    ["#76FF03", "#FFC400"],
  default:   ["#B026FF", "#00F0FF"],
};

function hash(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function gradientFor(name = "", category = "default") {
  const [a, b] = PALETTES[category] || PALETTES.default;
  const angle = 25 + (hash(name) % 120); // 25–145deg
  return `linear-gradient(${angle}deg, ${a}, ${b})`;
}

/**
 * Avatar — gradient card with initials + optional emoji symbol.
 * Props: name, category, size (css), symbol, rounded, className
 */
export default function Avatar({
  name = "",
  category = "default",
  size = 56,
  symbol,
  rounded = 14,
  className = "",
  showSymbol = true,
}) {
  const bg = gradientFor(name, category);
  const style = {
    width: size,
    height: size,
    borderRadius: rounded,
    background: bg,
  };
  const initials = initialsOf(name);
  const fontSize = Math.max(12, Math.floor(size * 0.36));
  return (
    <div
      className={`relative overflow-hidden shrink-0 ring-1 ring-white/15 ${className}`}
      style={style}
      aria-label={name}
    >
      {/* glossy highlight */}
      <span
        aria-hidden
        className="absolute inset-0"
        style={{ background: "radial-gradient(120% 80% at 15% 0%, rgba(255,255,255,0.3), transparent 55%)" }}
      />
      {/* grain-ish */}
      <span
        aria-hidden
        className="absolute inset-0 mix-blend-overlay opacity-30"
        style={{ background: "radial-gradient(circle at 80% 120%, rgba(0,0,0,0.6), transparent 55%)" }}
      />
      <span
        className="relative z-10 w-full h-full grid place-items-center font-display font-bold tracking-tight text-white"
        style={{ fontSize, textShadow: "0 1px 10px rgba(0,0,0,0.25)" }}
      >
        {initials}
      </span>
      {showSymbol && symbol && (
        <span
          className="absolute bottom-0.5 right-0.5 z-20 bg-black/60 backdrop-blur rounded-full grid place-items-center"
          style={{ width: Math.max(16, size * 0.32), height: Math.max(16, size * 0.32), fontSize: Math.max(10, size * 0.2) }}
          aria-hidden
        >
          {symbol}
        </span>
      )}
    </div>
  );
}

/** Large "hero" avatar that fills a banner area with a big gradient + initials in background */
export function HeroAvatar({ name, category, symbol, height = 224 }) {
  const bg = gradientFor(name, category);
  const initials = initialsOf(name);
  return (
    <div className="relative overflow-hidden" style={{ height, background: bg }}>
      <span aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(120% 100% at 10% 0%, rgba(255,255,255,0.3), transparent 55%)" }} />
      <span aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,8,0.0), rgba(5,5,8,0.95))" }} />
      <div aria-hidden className="absolute -right-8 -top-6 select-none font-display font-bold leading-none text-white/10" style={{ fontSize: height * 1.3 }}>
        {initials}
      </div>
      {symbol && (
        <div className="absolute top-4 right-4 text-3xl opacity-90" aria-hidden>{symbol}</div>
      )}
    </div>
  );
}
